/**
 * Pure traversal helpers for `PropertyFilterGroup`. Recursive and
 * mutation-free so callers can memoize the result.
 */

import type {
  PropertyFilter,
  PropertyFilterGroup,
} from "./searchSideBarConnector";

export interface CollectPropertyKeysOptions {
  /**
   * `propertyKey` values to skip. Useful when a specific attribute
   * shouldn't drive column auto-reveal (e.g. chart-driven chips whose
   * source is already visible elsewhere in the UI).
   */
  readonly exclude?: ReadonlySet<string>;
}

/**
 * Collects every `propertyKey` present in the filter tree into `out`.
 * Descends through nested `PropertyFilterGroup`s. Skips filters whose
 * `propertyKey` is empty or listed in `options.exclude`.
 */
export function collectPropertyKeys(
  group: PropertyFilterGroup | undefined,
  out: Set<string>,
  options?: CollectPropertyKeysOptions
): void {
  if (group === undefined) {
    return;
  }
  const exclude = options?.exclude;
  for (const f of group.filters ?? []) {
    if (f.type === "PropertyFilter") {
      const pf = f as PropertyFilter;
      if (
        typeof pf.propertyKey === "string" &&
        pf.propertyKey !== "" &&
        (exclude === undefined || !exclude.has(pf.propertyKey))
      ) {
        out.add(pf.propertyKey);
      }
    } else if (f.type === "PropertyFilterGroup") {
      collectPropertyKeys(f as PropertyFilterGroup, out, options);
    }
  }
}
